import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import GenericCategoryDropdown from './GenericCategoryDropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { RadioButton } from 'primereact/radiobutton';

import { useGetMainCategoriesQuery, useGetSecondCategoriesQuery, useGetSubCategoriesQuery } from '../../../../api/endpoints/faultcodes';
import { useLanguage } from '../../../../contexts/LanguageContext';

export const CATEGORY_LEVELS = {
    MAIN: 'MAIN',
    SECONDARY: 'SECONDARY',
    SUB: 'SUB',
    SUB_PART: 'SUB_PART',
    FAILURE_MODES: 'FAILURE'
};

const getFlagClassNameForDialog = (languageCode) => {
    if (!languageCode) return 'fi fi-xx';
    const codeToFlagSuffixMap = {
        en: 'us',
        tr: 'tr',
        de: 'de',
        fr: 'fr',
        it: 'it',
        es: 'es',
        ro: 'ro'
    };
    const lowerCaseCode = languageCode.toLowerCase();
    const flagSuffix = codeToFlagSuffixMap[lowerCaseCode];
    return flagSuffix ? `fi fi-${flagSuffix}` : `fi fi-${lowerCaseCode}`;
};

const AllCategoryDialog = ({ visible, onHide, formData, handleInputChange, onSubmit, formSubmitted, isSubmitting, isEditMode, supportedLanguages = [], dialogError, categoryLevel, executeDelete, isDeleting }) => {
    const { th } = useLanguage();

    const getDialogTitle = useCallback(() => {
        let levelTranslationKey = '';
        switch (categoryLevel) {
            case CATEGORY_LEVELS.MAIN:
                levelTranslationKey = 'faultCodes.mainCategory';
                break;
            case CATEGORY_LEVELS.SECONDARY:
                levelTranslationKey = 'faultCodes.secondCategory';
                break;
            case CATEGORY_LEVELS.SUB:
                levelTranslationKey = 'faultCodes.subCategory';
                break;
            case CATEGORY_LEVELS.SUB_PART:
                levelTranslationKey = 'faultCodes.subPartCategory';
                break;
            case CATEGORY_LEVELS.FAILURE_MODES:
                levelTranslationKey = 'faultCodes.failureMode';
                break;
            default:
                levelTranslationKey = 'faultCodes.mainCategory';
        }

        const categoryName = th(levelTranslationKey);
        const actionKey = isEditMode ? 'faultCodes.edit' : 'faultCodes.add';
        const actionText = th(actionKey, { category: categoryName });
        const entityId = formData?.id ? ` (ID: ${formData.id})` : '';

        return `${actionText}${entityId}`;
    }, [categoryLevel, isEditMode, formData?.id, th]);

    const [mainResetCount, setMainResetCount] = useState(0);
    const [secondResetCount, setSecondResetCount] = useState(0);
    const [subResetCount, setSubResetCount] = useState(0);

    const prevMainCategoryRef = useRef(formData.mainCategory);
    useEffect(() => {
        if (prevMainCategoryRef.current !== formData.mainCategory) {
            setSecondResetCount((c) => c + 1);
            setSubResetCount((c) => c + 1);
        }
        prevMainCategoryRef.current = formData.mainCategory;
    }, [formData.mainCategory]);

    const prevSecondaryCategoryRef = useRef(formData.secondaryCategory);
    useEffect(() => {
        if (prevSecondaryCategoryRef.current !== formData.secondaryCategory) {
            setSubResetCount((c) => c + 1);
        }
        prevSecondaryCategoryRef.current = formData.secondaryCategory;
    }, [formData.secondaryCategory]);

    const prevSubCategoryRef = useRef(formData.subCategory);
    useEffect(() => {
        prevSubCategoryRef.current = formData.subCategory;
    }, [formData.subCategory]);

    const isNewRecordMode = !isEditMode && !formData.id;
    useEffect(() => {
        if (isNewRecordMode && visible) {
            handleInputChange({ target: { value: null } }, 'mainCategory');
            handleInputChange({ target: { value: null } }, 'secondaryCategory');
            handleInputChange({ target: { value: null } }, 'subCategory');
            if (formData.isActive === undefined || formData.isActive === null) {
                handleInputChange({ target: { name: 'isActive', value: 1 } }, 'isActive');
            }
            setMainResetCount((c) => c + 1);
            setSecondResetCount((c) => c + 1);
            setSubResetCount((c) => c + 1);
        }
    }, [isNewRecordMode, visible, handleInputChange, formData.isActive]);

    const confirmDeletion = useCallback(() => {
        if (!formData || !formData.id || !executeDelete) {
            return;
        }
        const recordName = formData.translations?.en?.text || formData.translations?.tr?.text || `ID: ${formData.id}`;

        let categoryTranslationKey = '';
        switch (categoryLevel) {
            case CATEGORY_LEVELS.MAIN:
                categoryTranslationKey = 'faultCodes.mainCategory';
                break;
            case CATEGORY_LEVELS.SECONDARY:
                categoryTranslationKey = 'faultCodes.secondCategory';
                break;
            case CATEGORY_LEVELS.SUB:
                categoryTranslationKey = 'faultCodes.subCategory';
                break;
            case CATEGORY_LEVELS.SUB_PART:
                categoryTranslationKey = 'faultCodes.subPartCategory';
                break;
            case CATEGORY_LEVELS.FAILURE_MODES:
                categoryTranslationKey = 'faultCodes.failureMode';
                break;
            default:
                categoryTranslationKey = 'faultCodes.mainCategory';
        }

        const categoryName = th(categoryTranslationKey);
        // Ana/ikincil/alt kategoriler silinince bağlı alt kategoriler de silinir (cascade).
        // Alt-parça ve arıza modlarının alt kategorisi olmadığı için normal mesaj gösterilir.
        const hasChildCategories =
            categoryLevel === CATEGORY_LEVELS.MAIN ||
            categoryLevel === CATEGORY_LEVELS.SECONDARY ||
            categoryLevel === CATEGORY_LEVELS.SUB;
        const confirmMessageKey = hasChildCategories ? 'faultCodes.deleteConfirmWithChildren' : 'faultCodes.deleteConfirm';
        const confirmMessage = th(confirmMessageKey, { category: categoryName });

        confirmDialog({
            message: `"${recordName}" ${confirmMessage}`,
            header: th('common.deleteConfirmTitle'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: th('common.yes'),
            rejectLabel: th('common.no'),
            accept: executeDelete,
            reject: () => { },
            rejectClassName: 'p-button-outlined'
        });
    }, [formData, executeDelete, categoryLevel, th]);

    const dialogFooterContent = useMemo(() => {
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <div>{isEditMode && formData && formData.id && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" onClick={confirmDeletion} loading={isDeleting} disabled={isSubmitting || isDeleting} />}</div>
                <div>
                    <Button outlined label={th('common.cancel')} icon="pi pi-times" onClick={onHide} disabled={isSubmitting || isDeleting} />
                    <Button label={isEditMode ? th('common.save') : th('common.add')} icon="pi pi-check" onClick={onSubmit} loading={isSubmitting} disabled={isDeleting || isSubmitting} />
                </div>
            </div>
        );
    }, [onHide, onSubmit, isSubmitting, isEditMode, formData, isDeleting, confirmDeletion, th]);

    const sortedLanguages = useMemo(() => {
        if (!supportedLanguages || supportedLanguages.length === 0) return [];
        return [...supportedLanguages].sort((a, b) => {
            if (a.code.toLowerCase() === 'en') return -1;
            if (b.code.toLowerCase() === 'en') return 1;
            if (a.code.toLowerCase() === 'tr') return -1;
            if (b.code.toLowerCase() === 'tr') return 1;
            return (a.name || a.code).localeCompare(b.name || b.code);
        });
    }, [supportedLanguages]);

    const handleDropdownSelectionChange = useCallback(
        (fieldName, selectedObject) => {
            handleInputChange({ target: { name: fieldName, value: selectedObject } }, fieldName);
            if (fieldName === 'mainCategory') {
                handleInputChange({ target: { name: 'secondaryCategory', value: null } }, 'secondaryCategory');
                handleInputChange({ target: { name: 'subCategory', value: null } }, 'subCategory');
            } else if (fieldName === 'secondaryCategory') {
                handleInputChange({ target: { name: 'subCategory', value: null } }, 'subCategory');
            }
        },
        [handleInputChange]
    );

    const onMainCategoryChangeInternal = useCallback((selectedObject) => handleDropdownSelectionChange('mainCategory', selectedObject), [handleDropdownSelectionChange]);
    const onSecondCategoryChangeInternal = useCallback((selectedObject) => handleDropdownSelectionChange('secondaryCategory', selectedObject), [handleDropdownSelectionChange]);
    const onSubCategoryChangeInternal = useCallback((selectedObject) => handleDropdownSelectionChange('subCategory', selectedObject), [handleDropdownSelectionChange]);

    const isFieldRequired = useCallback(
        (level) => {
            if (!categoryLevel) return false;
            if (categoryLevel === CATEGORY_LEVELS.MAIN && level === CATEGORY_LEVELS.MAIN) return true;
            if (categoryLevel === CATEGORY_LEVELS.FAILURE_MODES && level === CATEGORY_LEVELS.FAILURE_MODES) return true;
            if (categoryLevel === CATEGORY_LEVELS.SECONDARY && level === CATEGORY_LEVELS.MAIN) return true;
            if (categoryLevel === CATEGORY_LEVELS.SUB && (level === CATEGORY_LEVELS.MAIN || level === CATEGORY_LEVELS.SECONDARY)) return true;
            if (categoryLevel === CATEGORY_LEVELS.SUB_PART && (level === CATEGORY_LEVELS.MAIN || level === CATEGORY_LEVELS.SECONDARY || level === CATEGORY_LEVELS.SUB)) return true;

            return false;
        },
        [categoryLevel]
    );

    const dataSelectorForCategories = useCallback((apiResponse) => {
        return apiResponse?.data || [];
    }, []);

    const showMainDropdown = categoryLevel === CATEGORY_LEVELS.SECONDARY || categoryLevel === CATEGORY_LEVELS.SUB || categoryLevel === CATEGORY_LEVELS.SUB_PART;
    const showSecondDropdown = categoryLevel === CATEGORY_LEVELS.SUB || categoryLevel === CATEGORY_LEVELS.SUB_PART;
    const showSubDropdown = categoryLevel === CATEGORY_LEVELS.SUB_PART;

    const anyParentDropdownVisible = showMainDropdown || showSecondDropdown || showSubDropdown;
    const itemIdFieldForCategories = 'lookupCode';
    const PAGE_LOAD_ROW_COUNT = 10;
    const isFormDisabled = isSubmitting || isDeleting;

    return (
        <React.Fragment>
            <ConfirmDialog />
            <Dialog
                header={getDialogTitle()}
                visible={visible}
                style={{ width: 'clamp(500px, 60vw, 750px)' }}
                modal
                className="p-fluid"
                maximizable={true}
                contentStyle={{ maxHeight: '75vh', overflowY: 'auto' }}
                footer={dialogFooterContent}
                onHide={onHide}
            >
                {showMainDropdown && (
                    <div className="field mb-3">
                        <label htmlFor="dialogFormMainCategory" className="font-semibold">
                            {th('faultCodes.mainCategory')} {isFieldRequired(CATEGORY_LEVELS.MAIN) && <span className="text-red-500">*</span>}
                        </label>
                        <GenericCategoryDropdown
                            value={formData.mainCategory}
                            onChange={onMainCategoryChangeInternal}
                            useQueryHook={useGetMainCategoriesQuery}
                            baseQueryArgs={{}}
                            dataSelector={dataSelectorForCategories}
                            itemLabelField="mainCategoryName"
                            itemIdField={itemIdFieldForCategories}
                            placeholder={th('faultCodes.common.selectMainCategory')}
                            disabled={isFormDisabled}
                            resetTrigger={mainResetCount}
                            skipQuery={false}
                            rowCount={PAGE_LOAD_ROW_COUNT}
                            className={`w-full ${formSubmitted && isFieldRequired(CATEGORY_LEVELS.MAIN) && !formData.mainCategory ? 'p-invalid' : ''}`}
                        />
                        {formSubmitted && isFieldRequired(CATEGORY_LEVELS.MAIN) && !formData.mainCategory && <small className="p-error">{th('faultCodes.validation.mainCategoryRequired')}</small>}
                    </div>
                )}

                {showSecondDropdown && (
                    <div className="field mb-3">
                        <label htmlFor="dialogFormSecondCategory" className="font-semibold">
                            {th('faultCodes.secondCategory')} {isFieldRequired(CATEGORY_LEVELS.SECONDARY) && <span className="text-red-500">*</span>}
                        </label>
                        <GenericCategoryDropdown
                            value={formData.secondaryCategory}
                            onChange={onSecondCategoryChangeInternal}
                            useQueryHook={useGetSecondCategoriesQuery}
                            baseQueryArgs={{ mainCategory: typeof formData.mainCategory === 'object' ? formData.mainCategory?.[itemIdFieldForCategories] : formData.mainCategory }}
                            dataSelector={dataSelectorForCategories}
                            itemLabelField="secondaryCategoryName"
                            itemIdField={itemIdFieldForCategories}
                            placeholder={th('faultCodes.common.selectSecondCategory')}
                            disabled={!formData.mainCategory || isFormDisabled}
                            resetTrigger={secondResetCount}
                            skipQuery={!formData.mainCategory}
                            rowCount={PAGE_LOAD_ROW_COUNT}
                            className={`w-full ${formSubmitted && isFieldRequired(CATEGORY_LEVELS.SECONDARY) && !formData.secondaryCategory ? 'p-invalid' : ''}`}
                        />
                        {formSubmitted && isFieldRequired(CATEGORY_LEVELS.SECONDARY) && !formData.secondaryCategory && <small className="p-error">{th('faultCodes.validation.secondCategoryRequired')}</small>}
                    </div>
                )}

                {showSubDropdown && (
                    <div className="field mb-3">
                        <label htmlFor="dialogFormSubCategory" className="font-semibold">
                            {th('faultCodes.subCategory')} {isFieldRequired(CATEGORY_LEVELS.SUB) && <span className="text-red-500">*</span>}
                        </label>
                        <GenericCategoryDropdown
                            value={formData.subCategory}
                            onChange={onSubCategoryChangeInternal}
                            useQueryHook={useGetSubCategoriesQuery}
                            baseQueryArgs={{
                                mainCategory: typeof formData.mainCategory === 'object' ? formData.mainCategory?.[itemIdFieldForCategories] : formData.mainCategory,
                                secondaryCategory: typeof formData.secondaryCategory === 'object' ? formData.secondaryCategory?.[itemIdFieldForCategories] : formData.secondaryCategory
                            }}
                            dataSelector={dataSelectorForCategories}
                            itemLabelField="subCategoryName"
                            itemIdField={itemIdFieldForCategories}
                            placeholder={th('faultCodes.common.selectSubCategory')}
                            disabled={!formData.mainCategory || !formData.secondaryCategory || isFormDisabled}
                            resetTrigger={subResetCount}
                            skipQuery={!formData.mainCategory || !formData.secondaryCategory}
                            rowCount={PAGE_LOAD_ROW_COUNT}
                            className={`w-full ${formSubmitted && isFieldRequired(CATEGORY_LEVELS.SUB) && !formData.subCategory ? 'p-invalid' : ''}`}
                        />
                        {formSubmitted && isFieldRequired(CATEGORY_LEVELS.SUB) && !formData.subCategory && <small className="p-error">{th('faultCodes.validation.subCategoryRequired')}</small>}
                    </div>
                )}

                {anyParentDropdownVisible && <hr className="my-3" />}

                {!anyParentDropdownVisible && categoryLevel === CATEGORY_LEVELS.MAIN && (
                    <>
                        <p className="text-sm text-color-secondary mt-1 mb-3">{th('faultCodes.noParentCategory')}</p>
                        <hr className="my-3" />
                    </>
                )}

                <div className="field mb-3">
                    <label className="font-semibold mb-2 d-block">{th('common.status')}</label>
                    <div className="flex flex-wrap gap-3">
                        <div className="flex align-items-center">
                            <RadioButton inputId="statusActive" name="isActive" value={1} onChange={(e) => handleInputChange({ target: { name: 'isActive', value: e.value } }, 'isActive')} checked={formData.isActive === 1} disabled={isFormDisabled} />
                            <label htmlFor="statusActive" className="ml-2">
                                {th('common.active')}
                            </label>
                        </div>
                        <div className="flex align-items-center">
                            <RadioButton
                                inputId="statusPassive"
                                name="isActive"
                                value={0}
                                onChange={(e) => handleInputChange({ target: { name: 'isActive', value: e.value } }, 'isActive')}
                                checked={formData.isActive === 0}
                                disabled={isFormDisabled}
                            />
                            <label htmlFor="statusPassive" className="ml-2">
                                {th('common.inactive')}
                            </label>
                        </div>
                    </div>
                </div>

                {sortedLanguages.length > 0 && <hr className="my-3" />}

                {sortedLanguages.length > 0 && (
                    <div className="mt-3">
                        {sortedLanguages.map((lang) => {
                            const langCode = lang.code?.toLowerCase() || '';
                            const translationEntry = Object.values(formData.translations || {}).find((item) => item.languageId === lang.id);
                            const currentText = translationEntry?.text || '';
                            let isNameRequiredForLang = false;
                            if (langCode === 'tr' || langCode === 'en') {
                                isNameRequiredForLang = true;
                            }

                            return (
                                <div key={lang.id || lang.code} className="grid align-items-center mb-3">
                                    <div className="col-fixed" style={{ width: '45px' }}>
                                        <span className={getFlagClassNameForDialog(langCode)} style={{ fontSize: '1.8em' }} title={lang.name || langCode.toUpperCase()}></span>
                                    </div>
                                    <div className="col">
                                        <div className="font-semibold mb-1">
                                            <span style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                                                {lang.name || langCode.toUpperCase()}
                                                {isNameRequiredForLang && <span className="text-red-500">*</span>}
                                            </span>
                                        </div>
                                        <InputTextarea
                                            id={`text_input_${langCode}`}
                                            name={`translations.${langCode}.text`}
                                            value={currentText}
                                            onChange={(e) => {
                                                handleInputChange(e, `translations.${langCode}.text`);
                                            }}
                                            rows={1}
                                            autoResize
                                            disabled={isFormDisabled}
                                            className={`w-full ${currentText.trim() ? 'font-bold' : ''} ${formSubmitted && isNameRequiredForLang && !currentText.trim() ? 'p-invalid' : ''}`}
                                        />
                                        {formSubmitted && isNameRequiredForLang && !currentText.trim() && <small className="p-error">{th('faultCodes.validation.categoryNameRequired', { language: lang.name || langCode.toUpperCase() })}</small>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {dialogError && <p className={`p-error text-center mt-4`}>{typeof dialogError === 'string' ? dialogError : dialogError.data?.message || dialogError.message || th('common.operationFailedError')}</p>}
            </Dialog>
        </React.Fragment>
    );
};

export default AllCategoryDialog;